import {FC, useContext, useEffect, useState} from 'react';
import {DirectionalHint, Icon, TooltipHost} from '@fluentui/react';
import {GlobalSettingsContext} from '../../../providers/GlobalSettingsContext';
import {useUser} from '../../../hooks/services/useUser';
import styles from './FloatingChatbot.module.scss';
import { useNavigation } from '../../common/Navigation/useNavigation';


type ChatbotState = 'minimized' | 'windowed' | 'maximized';

export const FloatingChatbot: FC = () => {
  const {globalSettings} = useContext(GlobalSettingsContext);
  const {getCurrentUser} = useUser();
  const [chatbotState, setChatbotState] = useState<ChatbotState>('minimized');
  const {showChatbot, setShowChatbot, hideIcon, setHideIcon} = useNavigation();
  const [showBubble, setShowBubble] = useState(false);
  const [chatbotUrl, setChatbotUrl] = useState<string>('');
  
  const chatbotFlag = globalSettings.settings.chatbot === 'true';
  const unitId = globalSettings.unitId;


  useEffect(() => {
    if(showChatbot) {
      setChatbotState('windowed');
    }
  },[showChatbot]);


  useEffect(() => {
    if (!chatbotFlag) return;
    const hide = sessionStorage.getItem('hide-chatbot-icon') === 'true';
    hide ? setHideIcon(true) : setHideIcon(false);
  }, [chatbotFlag, setHideIcon]);


  useEffect(() => {
    const imsChatbotTooltip = localStorage.getItem('IMSChatbotTooltip');
    if (imsChatbotTooltip) {
      try {
        const savedAt = new Date(imsChatbotTooltip).getTime();
        const now = new Date().getTime();
        const diffDays = (now - savedAt) / (1000 * 60 * 60 * 24);
        
        if (diffDays <= 7) return;
        localStorage.removeItem('IMSChatbotTooltip');
      } catch {
        localStorage.removeItem('IMSChatbotTooltip');
      }
    }
    
    if (!chatbotFlag || chatbotState !== 'minimized') return;

    const hasSeenBubble = sessionStorage.getItem('bottom-chatbot-bubble-seen');
    if (hasSeenBubble) {
      return;
    }
    
    setShowBubble(true);
    sessionStorage.setItem('bottom-chatbot-bubble-seen', 'true');
    const timer = setTimeout(() => setShowBubble(false), 10000);
    return () => clearTimeout(timer);
  }, [chatbotFlag, chatbotState]);

  useEffect(() => {
    const fetchChatbotUrl = async () => {

      if(chatbotState === 'minimized') {
        return;
      }
      
      try {
        const user = await getCurrentUser();
        const sid = user?.idTokenClaims?.sid ?? '';
        const username = user?.username ?? '';

        const tokenExpirationDate = user?.idTokenClaims?.exp;
        const now = new Date().getTime();

        if (tokenExpirationDate === undefined || tokenExpirationDate * 1000 < now) {
          window.location.reload();
        }

        let url = '';
        if (unitId) {
          url = `${process.env.REACT_APP_CHATBOT_URL}?instance=${process.env.REACT_APP_DIVISION_NAME}&unitnumber=${unitId}&sid=${sid}&username=${username}`;
        } else {
          url = `${process.env.REACT_APP_CHATBOT_URL}?instance=${process.env.REACT_APP_DIVISION_NAME}&sid=${sid}&username=${username}`;
        }
        setChatbotUrl(url);
        
      } catch (error) {
        console.error('Error fetching chatbot URL:', error);
      }
    };

    if (chatbotState !== 'minimized') {
      fetchChatbotUrl();
    const interval = setInterval(fetchChatbotUrl, 300000); // 5 minutes
    return () => clearInterval(interval);
    }
  }, [chatbotState, unitId, getCurrentUser]);

  const handleCloseBubble = () => {
    setShowBubble(false);
    localStorage.setItem('IMSChatbotTooltip', new Date().toISOString());
  };

    const handleClose = () => {
    setChatbotState('minimized');
    setShowChatbot(false);
    setChatbotUrl(''); 
  };

  const handleMinimize = () => {
    setChatbotState('minimized');
    setHideIcon(false);
    sessionStorage.removeItem('hide-chatbot-icon');
    setShowChatbot(false);
  };

  const handleMaximize = () => {
    setChatbotState('maximized');
  };

  const handleOpenWindow = () => {
    setShowBubble(false);
    setChatbotState('windowed');
  };

  const handleHideIcon = () => {
    sessionStorage.setItem('hide-chatbot-icon', 'true');
    setHideIcon(true);
  }

  if (!chatbotFlag) return null;

  return (
    <>
      {chatbotState === 'minimized' && hideIcon === false && (
        <div className={styles.floatingChatbot}>
          <TooltipHost content={showBubble ? '' : 'Chatbot'}>
            {showBubble && (
              <div className={styles.bubble}>
                Need help? <br />
                No need to search. <br />
                Try our IMS Chatbot!
                <button onClick={handleCloseBubble}>×</button>
              </div>
            )}
             <div className={styles.chatbotIcon} onClick={handleOpenWindow}>
              <Icon iconName="ChatBot" />
            </div>
          </TooltipHost>
          <TooltipHost content={'Close chatbot icon'} directionalHint={DirectionalHint.rightCenter}>
            <div className={styles.closeIcon} onClick={handleHideIcon}>x</div>
            </TooltipHost>
        </div>
      )}

      {chatbotState === 'windowed' && (
        <div className={styles.chatbotWindow}>
          <div className={styles.windowHeader}>
            <span className={styles.windowTitle}>
              <Icon iconName="ChatBot" className={styles.titleIcon} />
              IMS Chatbot
            </span>
            <div className={styles.windowControls}>
              <button 
                onClick={handleMinimize} 
                title="Minimize"
                className={styles.controlButton}
              >
                <Icon iconName="ChromeMinimize" />
              </button>
              <button 
                onClick={handleMaximize} 
                title="Maximize"
                className={styles.controlButton}
              >
                <Icon iconName="FullScreen" />
              </button>
              <button 
                onClick={handleClose} 
                title="Close and end conversation"
                className={`${styles.controlButton} ${styles.closeButton}`}
              >
                <Icon iconName="Cancel" />
              </button>
            </div>
          </div>
          <div className={styles.windowContent} />
        </div>
      )}
      {chatbotState === 'maximized' && (
        <div className={styles.modalOverlay}>
        <div className={styles.chatbotMaximized}>
          <div className={styles.maximizedHeader}>
            <span className={styles.windowTitle}>
              <Icon iconName="ChatBot" className={styles.titleIcon} />
              IMS Chatbot
            </span>
            <div className={styles.windowControls}>
              <button 
                onClick={() => setChatbotState('windowed')} 
                title="Restore"
                className={styles.controlButton}
              >
                <Icon iconName="BackToWindow" />
              </button>
              <button 
                onClick={handleClose} 
                title="Close"
                className={`${styles.controlButton} ${styles.closeButton}`}
              >
                <Icon iconName="Cancel" />
              </button>
            </div>
          </div>
          <div className={styles.maximizedContent} />
        </div>
        </div>
      )}

      {chatbotUrl && (
        <iframe
          allow="clipboard-write"
          key="chatbot-single-instance"
          className={`${styles.chatbotIframe} ${
            chatbotState === 'minimized' 
              ? styles.iframeHidden 
              : chatbotState === 'windowed' 
              ? styles.iframeWindowed 
              : styles.iframeMaximized
          }`}
          title="Chatbot"
          src={chatbotUrl}
        />
      )}
    </>
  );
};